import React, { useEffect, useMemo, useState } from "react";
import { Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import {
    deleteAccountDetailsById,
    getAllAccountDetails,
    getAllAccountDetailsForExport,
} from "../../services/contractorApi";
import DynamicFilter from "../../components/DynamicFilter/DynamicFilter";
import { toast } from "react-toastify";
import ConfirmAlert from "../../components/ConfirmAlert";
import ImageViewer from "../../components/ImageViewer";
import { useTranslation } from "react-i18next";
import ActionButtons from "../../components/DataTable/ActionButtons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import TableHeader from "../../components/DataTable/TableHeader";
import CustomTable from "../../components/DataTable/CustomTable";
import { createColumnHelper } from "@tanstack/react-table";
import { serialNumber } from "../../utils/helper";
import Tabs, { Tab } from "react-best-tabs";
import UserBankAccounts from "./UserBankAccounts";
import { formatNumberToINR } from "../../utils/helper";
// import Creditors from "./Creditors";
// SupplierAccounts relocated to Supplier Management → "Supplier Reports" tab.

import { useLocation } from "react-router-dom";

// checkPermission = "Accounts - Bank Accounts" row; userAccountsPermission =
// "Accounts - User Accounts" row (falls back to checkPermission when the
// caller has no separate row).
const AccountManagement = ({ checkPermission, userAccountsPermission }) => {
    const userAccountsPerm = userAccountsPermission || checkPermission;
    const [showAlert, setShowAlert] = useState(false);
    const [idToDelete, setIdToDelete] = useState("");

    const location = useLocation();
    const columnHelper = createColumnHelper();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pageNo = searchParams.get("pageNo") || 1;
    const pageSize = searchParams.get("pageSize") || 10;
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const { userPermission } = useSelector(selectUser);
    const user_id = useSelector((state) => state.auth.user.id);
    const user_type = useSelector((state) => state.auth.user.user_type);
    const [activeTab, setActiveTab] = useState(() => {
        const last = localStorage.getItem("last_tab");
        // "4" was the removed "Supplier Accounts" tab (now in Supplier
        // Management → Supplier Reports). Fall back to Bank Accounts.
        return !last || last === "4" ? "2" : last;
    });

    // Filter state for the Bank Accounts tab — selected values keyed by the
    // backend param name. Reused by both the table fetch and the export call
    // so reports always reflect what the user is currently viewing.
    const [bankFilters, setBankFilters] = useState({});
    // Dropdown option lists derived from the full bank list.
    const [bankFilterOptions, setBankFilterOptions] = useState({
        bank_name: [],
        holder_name: [],
    });

    const buildBankFilterPayload = (custom = bankFilters) => ({
        bank_name: custom.bank_name || "",
        holder_name: custom.holder_name || "",
        account_status: custom.account_status || "",
        fromDate: custom.fromDate || "",
        toDate: custom.toDate || "",
    });

    const fetchAllBankData = async (custom = bankFilters) => {
         setIsLoading(true);
        const res = await getAllAccountDetailsForExport({
            search,
            pageSize,
            pageNo,
            user_id,
            user_type,
            ...buildBankFilterPayload(custom),
        });

        if (res.status) {
            setRows(res.data);
            setTotalData(res?.pageDetails?.total);
        } else {
            setRows([]);
            setTotalData(0);
        }
        setIsLoading(false);
    };

    // Populate the Bank Name / Holder Name dropdowns from the full list
    // (no pagination, all rows). One-time fetch on mount.
    const fetchBankFilterOptions = async () => {
        const res = await getAllAccountDetailsForExport({
            search: "",
            user_id,
            user_type,
            pageSize: 1000,
            pageNo: 1,
        });
        if (res?.status && Array.isArray(res.data)) {
            const seenBank = new Set();
            const bank_name = res.data
                .filter(
                    (r) =>
                        r.bank_name && !seenBank.has(r.bank_name) && seenBank.add(r.bank_name),
                )
                .map((r) => ({ label: r.bank_name, value: r.bank_name }));
            const seenHolder = new Set();
            const holder_name = res.data
                .filter(
                    (r) =>
                        r.account_holder_name &&
                        !seenHolder.has(r.account_holder_name) &&
                        seenHolder.add(r.account_holder_name),
                )
                .map((r) => ({
                    label: r.account_holder_name,
                    value: r.account_holder_name,
                }));
            setBankFilterOptions({ bank_name, holder_name });
        }
    };

    const handleAccountExport = (params) => {
        return getAllAccountDetailsForExport({
            ...params,
            search,
            user_id,
            user_type,
            ...buildBankFilterPayload(),
            // Strip frontend-only columns from the export payload — branded PDF
            // generator auto-renders SR, and action has no data field.
            columns: (() => {
                try {
                    const parsed = JSON.parse(params?.columns || "[]");
                    if (Array.isArray(parsed)) {
                        return JSON.stringify(
                            parsed.filter((c) => c !== "sr_no" && c !== "action"),
                        );
                    }
                } catch (_) {}
                return params?.columns;
            })(),
        });
    };

    const bankFilterFields = useMemo(
        () => [
            {
                key: "bank_name",
                label: "Bank Name",
                options: bankFilterOptions.bank_name,
                value: bankFilters.bank_name || "",
                type: "select",
            },
            {
                key: "holder_name",
                label: "Account Holder",
                options: bankFilterOptions.holder_name,
                value: bankFilters.holder_name || "",
                type: "select",
            },
            {
                key: "account_status",
                label: "Status",
                options: [
                    { label: "Active", value: "1" },
                    { label: "Inactive", value: "0" },
                ],
                value: bankFilters.account_status || "",
                type: "select",
            },
            {
                key: "fromDate",
                label: "From Date",
                value: bankFilters.fromDate || "",
                type: "date",
            },
            {
                key: "toDate",
                label: "To Date",
                value: bankFilters.toDate || "",
                type: "date",
            },
        ],
        [bankFilterOptions, bankFilters],
    );

    const handleBankFilterChange = async (key, value) => {
        const updated = { ...bankFilters, [key]: value?.value ?? value ?? "" };
        setBankFilters(updated);
        await fetchAllBankData(updated);
    };

    const handleDelete = async () => {
        const res = await deleteAccountDetailsById(idToDelete);
        if (res.status) {
            toast.success(res.message);
            setRows((prev) => prev.filter((dlt) => dlt.id !== +idToDelete));
            fetchAllBankData();
        } else {
            toast.error(res.message);
        }
        setIdToDelete("");
        setShowAlert(false);
    };

    useEffect(() => {
        fetchAllBankData();
    }, [search, pageNo, pageSize]);

    useEffect(() => {
        fetchBankFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = useMemo(
        () => [
            columnHelper.accessor("sr_no", {
                header: t("Sr No."),
                cell: (info) => {
                    const serialNumbers = serialNumber(pageNo, pageSize);
                    return serialNumbers[info.row.index];
                },
            }),
            columnHelper.accessor("bank_name", {
                header: t("Bank Name"),
                cell: (info) => (
                    <ImageViewer
                        src={
                            info.row.original?.logo
                                ? `${process.env.REACT_APP_API_URL}${info.row.original?.logo}`
                                : `${process.env.REACT_APP_API_URL}/assets/images/default-image.png`
                        }
                    >
                        <span className="d-flex align-items-center gap-2">
                            <img
                                width={30}
                                height={30}
                                className="my-bg object-fit p-1 rounded-circle"
                                src={
                                    info.row.original?.logo
                                        ? `${process.env.REACT_APP_API_URL}${info.row.original?.logo}`
                                        : `${process.env.REACT_APP_API_URL}/assets/images/default-image.png`
                                }
                            />{" "}
                            {info.row.original?.bank_name}
                        </span>
                    </ImageViewer>
                ),
            }),
            columnHelper.accessor("account_holder_name", {
                header: t("Holder Name"),
            }),
            columnHelper.accessor("account_number", {
                header: t("Account Number"),
            }),
            columnHelper.accessor("account_type", {
                header: t("Account Type"),
            }),
            columnHelper.accessor("branch", {
                header: t("Branch"),
            }),
         columnHelper.accessor("opening_balance", {
    header: "Opening Balance",
    cell: (info) => (
        <td style={{ color: "green" }} className="fw-bold">
            ₹ {Number(info.row.original.opening_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </td>
    ),
}),

            columnHelper.accessor("closing_balance", {
                header: "Closing Balance",
                cell: (info) => {
                    const amount = Number(info.row.original.closing_balance || 0);
                    const formattedAmount = amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                    return (
                        <td style={{ color: "green" }} className="fw-bold">
                            ₹ {formattedAmount}
                        </td>
                    );
                },
            }),

            // columnHelper.accessor("balance", {
            //     header: t("Closing Balance"),
            // }),
            columnHelper.accessor("status", {
                cell: (info) => (
                    <td>{info.row.original.
                        is_default
                        == "1" ? "Active" : "Inactive"}</td>
                ),
            }),

            columnHelper.accessor("action", {
                header: t("Action"),
                cell: (info) => (
                    <ActionButtons
                        actions={{
                            view: {
                                show: checkPermission?.view,
                                action: () =>
                                    navigate(
                                        `/account-management/view-transaction/${info.row.original?.id}?type=details`
                                    ),
                            },
                            edit: {
                                show: checkPermission?.update,
                                action: () =>
                                    navigate(
                                        `/account-management/create-account-management/${info.row.original?.id}`
                                    ),
                            },
                            delete: {
                                show: checkPermission?.delete,
                                action: () => {
                                    setIdToDelete(`${info.row.original?.id}`);
                                    setShowAlert(true);
                                },
                            },
                        }}
                    />
                ),
            }),
        ],
        [checkPermission, rows.length, pageNo, pageSize]
    );

    const handleClick = (e, tab) => {
        setActiveTab(tab);
        if (searchParams.has("pageNo")) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("pageNo");
            navigate({
                pathname: location.pathname,
                search: newParams.toString(),
            });
        }
        localStorage.setItem("last_tab", tab);
    };

    return (
        <>
            <Col md={12} data-aos={"fade-up"}>
                <Tabs
                    onClick={(e, tab) => handleClick(e, tab)}
                    activeTab={activeTab}
                    ulClassName="border-primary p-2 border-bottom"
                    activityClassName="bg-secondary"
                >
                    <Tab className="pe-none fs-15 fw-bold" />
                    <Tab className="ms-auto" title={[t("Bank Accounts")]}>
                        {activeTab == "2" && (
                            <>
                                <DynamicFilter
                                    fields={bankFilterFields}
                                    onChange={handleBankFilterChange}
                                />
                                <CustomTable
                                    id={"account"}
                                    isLoading={isLoading}
                                    rows={rows || []}
                                    columns={columns}
                                    pagination={{
                                        pageNo,
                                        pageSize,
                                        totalData,
                                    }}
                                    excelAction={true}
                                    pdfAction={true}
                                    apiForExcelPdf={handleAccountExport}
                                    customHeaderComponent={() => (
                                        <TableHeader
                                            userPermission={checkPermission}
                                            setSearchText={setSearch}
                                            button={{
                                                noDrop: true,
                                                to: `/account-management/create-account-management/new`,
                                                title: "Create",
                                            }}
                                        />
                                    )}
                                    tableTitleComponent={
                                        <div>
                                            <strong>All Banks</strong>
                                        </div>
                                    }
                                />
                            </>
                        )}
                    </Tab>

                    <Tab title={t("User Accounts")}>
                        {activeTab == "3" && (
                            <UserBankAccounts checkPermission={userAccountsPerm} />
                        )}
                    </Tab>
                    {/* <Tab title={t("Creditor Accounts")}>
                        {activeTab == "4" && (
                            // <UserBankAccounts checkPermission={checkPermission} />
                            <Creditors checkPermission={checkPermission} />
                        )}
                    </Tab> */}
                    {/* Supplier Accounts moved to Supplier Management → "Supplier
                        Reports" tab. See src/pages/Suppliers/SupplierReports.js. */}
                </Tabs>
            </Col>

            <ConfirmAlert
                size={"sm"}
                deleteFunction={handleDelete}
                hide={setShowAlert}
                show={showAlert}
                title={"Confirm Delete"}
                description={"Are you sure you want to delete this!!"}
            />
        </>
    );
};

export default AccountManagement;
